import React, { useState, useEffect, useCallback } from 'react';
import ByzzerModal from '@/components/ByzzerCSRModal';
import AsyncSelect from 'react-select/async';
import generalStyles from '@/components/General/Styles.module.scss';
import styles from './TpaFormModal.module.scss';
import API_STATES from '@/constants/StateConstants';
import { getMatchingCompanies, addTpa, editTpa } from '@/services/api/company.service';
import debounce from 'lodash.debounce';

function TpaFormModal({ show, onClose, onSuccess, companyId, tpa }) {
    const isEditMode = Boolean(tpa);
    const heading = isEditMode ? 'Edit TPA' : 'Add New TPA';

    const [clientCompany, setClientCompany] = useState(null);
    const [tpaLink, setTpaLink] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [apiState, setApiState] = useState(API_STATES.none);
    const [apiError, setApiError] = useState('');

    useEffect(() => {
        if (show) {
            if (isEditMode) {
                setClientCompany({ label: tpa.clientCompanyName, value: tpa.nsClientCompanyId });
                setTpaLink(tpa.tpaLink ?? '');
                setStartDate(tpa.startDate ? tpa.startDate.slice(0, 10) : '');
                setEndDate(tpa.endDate ? tpa.endDate.slice(0, 10) : '');
            } else {
                setClientCompany(null);
                setTpaLink('');
                setStartDate('');
                setEndDate('');
            }
            setApiState(API_STATES.none);
            setApiError('');
        }
    }, [show, tpa, isEditMode]);

    const loadCompanyOptions = useCallback(
        debounce((inputValue, callback) => {
            if (!inputValue) {
                callback([]);
                return;
            }
            getMatchingCompanies(inputValue).then((response) => {
                if (response.status === API_STATES.success) {
                    callback(
                        response.data
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((company) => ({ label: company.name, value: company.id }))
                    );
                } else {
                    callback([]);
                }
            });
        }, 400),
        []
    );

    const isDateValid = startDate && endDate && endDate >= startDate;
    const isFormValid = clientCompany && tpaLink.trim() && startDate && endDate && isDateValid;

    async function handleSubmit() {
        setApiState(API_STATES.loading);
        const payload = {
            clientCompanyId: clientCompany.value,
            clientCompanyName: clientCompany.label,
            tpaLink: tpaLink.trim(),
            startDate,
            endDate,
        };
        const response = isEditMode
            ? await editTpa(companyId, tpa.id, payload)
            : await addTpa(companyId, payload);

        if (response.status === API_STATES.success) {
            setApiState(API_STATES.none);
            onSuccess();
        } else {
            setApiState(API_STATES.error);
            setApiError(response.error?.response?.data?.message ?? 'An error occurred. Please try again.');
        }
    }

    return (
        <ByzzerModal
            show={show}
            heading={heading}
            headerType="normal"
            size="small"
            onClose={onClose}
            closeOnClickOutside={false}
        >
            <div className={styles.form}>
                <div className={generalStyles['input-wrapper']}>
                    <label>Client Company Name <span className={styles.required}>*</span></label>
                    <span className={generalStyles.formField}>
                        <AsyncSelect
                            cacheOptions
                            loadOptions={loadCompanyOptions}
                            value={clientCompany}
                            onChange={setClientCompany}
                            placeholder="Select a company..."
                            noOptionsMessage={({ inputValue }) =>
                                inputValue ? 'No companies match the search' : 'Start typing to search'
                            }
                            isDisabled={apiState === API_STATES.loading}
                        />
                    </span>
                </div>

                <div className={generalStyles['input-wrapper']}>
                    <label>TPA <span className={styles.required}>*</span></label>
                    <div className={generalStyles.input}>
                        <input
                            type="url"
                            placeholder="Enter TPA link URL"
                            value={tpaLink}
                            onChange={(e) => setTpaLink(e.target.value)}
                            disabled={apiState === API_STATES.loading}
                        />
                    </div>
                </div>

                <div className={styles.dateRow}>
                    <div className={generalStyles['input-wrapper']}>
                        <label>TPA Start Date <span className={styles.required}>*</span></label>
                        <div className={generalStyles.input}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                disabled={apiState === API_STATES.loading}
                            />
                        </div>
                    </div>
                    <div className={generalStyles['input-wrapper']}>
                        <label>TPA End Date <span className={styles.required}>*</span></label>
                        <div className={generalStyles.input}>
                            <input
                                type="date"
                                value={endDate}
                                min={startDate || undefined}
                                onChange={(e) => setEndDate(e.target.value)}
                                disabled={apiState === API_STATES.loading}
                            />
                        </div>
                    </div>
                </div>
                {startDate && endDate && !isDateValid && (
                    <span className={generalStyles.errorMessage}>End date must be on or after start date.</span>
                )}

                {apiState === API_STATES.error && (
                    <span className={generalStyles.errorMessage}>{apiError}</span>
                )}
            </div>

            <div className={styles.footer}>
                <button
                    className={generalStyles.submitButton}
                    onClick={onClose}
                    disabled={apiState === API_STATES.loading}
                >
                    Cancel
                </button>
                <button
                    className={styles.primaryButton}
                    onClick={handleSubmit}
                    disabled={!isFormValid || apiState === API_STATES.loading}
                >
                    {apiState === API_STATES.loading
                        ? 'Saving...'
                        : isEditMode
                        ? 'Save Changes'
                        : 'Add TPA'}
                </button>
            </div>
        </ByzzerModal>
    );
}

export default TpaFormModal;
